const ICON_RASTER_SIZE = 128;
const SVG_ANIMATION_ELEMENTS = new Set([
  "animate",
  "animateMotion",
  "animateTransform",
  "set",
]);

export function validatePublicationSvgStyle(source: string): void {
  const css = source.replace(/\/\*[\s\S]*?\*\//g, "");
  if (/\\|@import\b|@font-face\b/i.test(css))
    throw new Error("SVG 样式含有外部引用或转义字符，请使用内嵌普通样式");
  const urls = Array.from(css.matchAll(/url\s*\(([^)]*)\)/gi));
  if (
    (css.match(/url\s*\(/gi) || []).length !== urls.length ||
    urls.some((match) => {
      const value = match[1].trim().replace(/^(['"])(.*)\1$/, "$2");
      return !/^#[^\s"'<>`()]+$/.test(value);
    })
  )
    throw new Error("SVG 引用了外部资源，请将资源内嵌后重试");
}

export async function preparePublicationIcon(file: File): Promise<File> {
  if (file.type !== "image/svg+xml" && !/\.svg$/i.test(file.name)) return file;
  const source = await file.text();
  if (/<!DOCTYPE|<!ENTITY/i.test(source)) throw new Error("SVG 格式不受支持");
  const document = new DOMParser().parseFromString(source, "image/svg+xml");
  const svg = document.documentElement;
  if (svg.localName !== "svg" || document.querySelector("parsererror"))
    throw new Error("请粘贴完整的 SVG");
  // Keep embedded styles and base geometry; managed icons are static PNGs.
  for (const element of [svg, ...Array.from(svg.querySelectorAll("*"))]) {
    if (SVG_ANIMATION_ELEMENTS.has(element.localName)) {
      element.remove();
      continue;
    }
    if (
      ["script", "foreignObject", "image", "feImage", "a"].includes(
        element.localName,
      )
    )
      throw new Error(`SVG 含有 <${element.localName}>，请移除后重试`);
    if (element.localName === "style")
      validatePublicationSvgStyle(element.textContent || "");
    for (const attribute of Array.from(element.attributes)) {
      if (/^on/i.test(attribute.name))
        throw new Error("SVG 含有事件处理，请移除后重试");
      if (/href$/i.test(attribute.name) && !attribute.value.startsWith("#"))
        throw new Error("SVG 引用了外部资源，请将资源内嵌后重试");
      validatePublicationSvgStyle(attribute.value);
    }
  }
  if (!svg.getAttribute("viewBox")) {
    const width = parseFloat(svg.getAttribute("width") || "24");
    const height = parseFloat(svg.getAttribute("height") || "24");
    if (!(width > 0 && height > 0)) throw new Error("SVG 尺寸无效");
    svg.setAttribute("viewBox", `0 0 ${width} ${height}`);
  }
  svg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  svg.setAttribute("width", String(ICON_RASTER_SIZE));
  svg.setAttribute("height", String(ICON_RASTER_SIZE));
  const url = URL.createObjectURL(
    new Blob([new XMLSerializer().serializeToString(svg)], {
      type: "image/svg+xml",
    }),
  );
  try {
    const image = new Image();
    image.src = url;
    await image.decode();
    const canvas = window.document.createElement("canvas");
    canvas.width = canvas.height = ICON_RASTER_SIZE;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("图标转换失败，请重试");
    context.drawImage(image, 0, 0, ICON_RASTER_SIZE, ICON_RASTER_SIZE);
    const blob = await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob(
        (value) =>
          value ? resolve(value) : reject(new Error("图标转换失败，请重试")),
        "image/png",
      ),
    );
    return new File([blob], file.name.replace(/\.svg$/i, "") + ".png", {
      type: "image/png",
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}
